import {
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Space,
  Submenu,
  SubmenuToggle,
  SubmenuList,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';

import { AccountData, SaModalNameEnum, SaModalState } from './types';
import { AppForSa } from '@Types/administrator';
import { ProvisionApi } from '@Apis';
import { EnabledPolicyTypeEnum } from '@Types/provision';
import { useGlobalSettingStore } from '../../../models/useGlobalSettingModel';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import { advancedStyle } from '@Dashboard/AdvanceModal/advancedTooltip';
import { isInUIC } from '@Utils/common';
import { useLicenseStore } from '../../../models/useLicenseModel';
import { App } from '@Types/app';

function AddButton({
  accounts,
  refreshData,
  setSaModalState,
}: {
  accounts: AccountData[];
  refreshData: () => void;
  setSaModalState: (v: SaModalState) => void;
}) {
  const { t } = useTranslation('common');
  const { widthEditablePermission } = usePermissionStore();
  const { hasEmailAddOnLicense } = useLicenseStore();

  function onOpen(orgId: string, app: AppForSa) {
    if (app === AppForSa.ExchangeServer) {
      ProvisionApi.apiSaveProvisionInfo(
        App.ExchangeServer,
        orgId, //exchange server don't need this authCode
        EnabledPolicyTypeEnum.None,
        orgId
      )
        .then(() => {
          refreshData();
        })
        .catch(() => {
          window.showNotification({
            titleMsg: t('common.unknownError'),
            severity: 'error',
          });
        });
      return;
    }

    if (
      [AppForSa.ExchangeInline, AppForSa.GmailInline].includes(app) &&
      !hasEmailAddOnLicense
    ) {
      setSaModalState({
        name: SaModalNameEnum.NeedLicense,
        data: {
          app: app as AppForSa.ExchangeInline | AppForSa.GmailInline,
        },
      });
      return;
    }
    setSaModalState({
      name: SaModalNameEnum.Provision,
      data: {
        orgId,
        app,
      },
    });
  }

  return (
    <Flex direction="row" display="inline-flex" align="center">
      <Menu>
        <MenuButton
          variant="primary"
          borderTopLeftRadius="0"
          borderBottomLeftRadius="0"
          disabled={!widthEditablePermission(PermissionNameEnum.administrator)}
        >
          {t('common.action.label.add')}
          <Space width="16px" />
        </MenuButton>
        <MenuList width="max-content">
          {accounts.map((account) => (
            <OrgMenu key={account.orgId} account={account} onClick={onOpen} />
          ))}
        </MenuList>
      </Menu>
    </Flex>
  );
}

function OrgMenu({
  account,
  onClick,
}: {
  account: AccountData;
  onClick: (orgId: string, app: AppForSa) => void;
}) {
  const { t } = useTranslation('common');
  const { selectedOrgGlobalSetting: orgSt } = useGlobalSettingStore();
  const { hasSalesforceLicense } = useLicenseStore();

  //app has provisioned, should disable these menu items
  const svcSet = new Set(account.apps.map((app) => app.app));

  const _SERVICES: AppForSa[] = [
    AppForSa.Exchange,
    AppForSa.ExchangeInline,
    AppForSa.OneDrive,
    AppForSa.SharePoint,
    AppForSa.Teams,
    AppForSa.TeamsChat,
    AppForSa.Gmail,
    AppForSa.GmailInline,
    AppForSa.GoogleDrive,
    AppForSa.Box,
    AppForSa.Dropbox,
    AppForSa.ExchangeServer,
    // AppForSa.SalesforceSandbox,
    // AppForSa.SalesforceProduction,
    // AppForSa.IdentityProtection,
    // AppForSa.Mip,
  ];

  const SERVICES = _SERVICES
    .filter((s) => s !== AppForSa.GmailInline || orgSt.gmailInlineFeatureShow)
    .filter(
      (s) => s !== AppForSa.ExchangeInline || orgSt.exchangeInlineFeatureShow
    )
    .filter(
      (s) => s !== AppForSa.ExchangeServer || orgSt.smexHybridFeatureShow
    );

  if (hasSalesforceLicense) {
    SERVICES.push(AppForSa.SalesforceSandbox, AppForSa.SalesforceProduction);
  }

  //需要检查需要migration的exchange online?
  const isMipShow = !![
    AppForSa.Exchange,
    AppForSa.SharePoint,
    AppForSa.OneDrive,
    AppForSa.DeprecatedSpod,
    AppForSa.Teams,
    AppForSa.TeamsChat,
  ].find((v) => svcSet.has(v));
  if (isMipShow) {
    SERVICES.push(AppForSa.Mip);
  }

  if (isMipShow) {
    const isIdentityProtectionShow = !![
      AppForSa.Exchange,
      AppForSa.SharePoint,
      AppForSa.OneDrive,
      AppForSa.DeprecatedSpod,
      AppForSa.Teams,
    ].find((v) => svcSet.has(v));
    if (isIdentityProtectionShow) {
      SERVICES.push(AppForSa.IdentityProtection);
    }
  }

  const handleMenuItemClick = (svc: AppForSa) => {
    if (svcSet.has(svc)) return;
    onClick(account.orgId, svc);
  };

  return (
    <Submenu key={account.orgId}>
      <SubmenuToggle width="100%">
        <MenuItem value={3}>{account.orgName}</MenuItem>
      </SubmenuToggle>
      <SubmenuList width="max-content">
        {SERVICES.map((svc) => (
          <MenuItem
            key={svc}
            value={svc}
            onClick={() => handleMenuItemClick(svc)}
            disabled={svcSet.has(svc)}
          >
            {(svc === AppForSa.ExchangeInline ||
              svc === AppForSa.GmailInline) &&
            isInUIC() ? (
              <>
                {t(`common.service.${svc}`)}
                <sup style={advancedStyle}>
                  {t(
                    'dashboard.serviceStatus.enableAdvancedProtection.superscript'
                  )}
                </sup>
              </>
            ) : (
              t(`common.service.${svc}`)
            )}
          </MenuItem>
        ))}
      </SubmenuList>
    </Submenu>
  );
}

export default AddButton;
